import React, { useRef, useEffect, useState } from 'react';
import config from '../config';
import {getBeHeaders} from '../utils/apiHeaders';
import { getAuthorizedFetch } from '../utils/fetchRegistry';
import './AIModeDropdown.css';

/**
 * Model Selector Dropdown Component
 * Allows selection from a list of LLM models (dynamically loaded from API)
 *
 * @param {Object} props
 * @param {string} props.model - Current selected model value
 * @param {Function} props.onModelChange - Callback when model changes
 * @param {boolean} props.isOpen - Whether dropdown is open
 * @param {Function} props.onToggle - Callback to toggle dropdown
 * @param {boolean} props.isLoading - Whether model selection is disabled
 */

/**
 * Helper component to render the correct SVG based on model ID
 */
const ModelIcon = ({ modelId, size = 20, className = "" }) => {
  const id = modelId?.toLowerCase() || '';

  // Icona Google Gemini (Scintilla)
  if (id.includes('gemini')) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="currentColor"
        width={size}
        height={size}
        className={className}
      >
        <path d="M11.05 2.14a.75.75 0 0 1 1.9 0l1.7 6.8a.75.75 0 0 0 .56.56l6.8 1.7a.75.75 0 0 1 0 1.9l-6.8 1.7a.75.75 0 0 0-.56.56l-1.7 6.8a.75.75 0 0 1-1.9 0l-1.7-6.8a.75.75 0 0 0-.56-.56l-6.8-1.7a.75.75 0 0 1 0-1.9l6.8-1.7a.75.75 0 0 0 .56-.56l1.7-6.8z" />
      </svg>
    );
  }

  // Icona Anthropic (Ae Logo)
  if (id.includes('claude')) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 16 16"
        fill="currentColor"
        width={size}
        height={size}
        className={className}
      >
        <path fillRule="evenodd" d="M9.218 2h2.402L16 12.987h-2.402zM4.379 2h2.512l4.38 10.987H8.82l-.895-2.308h-4.58l-.896 2.307H0L4.38 2.001zm2.755 6.64L5.635 4.777 4.137 8.64z"/>
      </svg>
    );
  }

  // GitHub Copilot Icon (GitHub mark)
  if (id.includes('gpt')) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 16 16"
        fill="currentColor"
        width={size}
        height={size}
        className={className}
      >
        <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z"/>
      </svg>
    );
  }

  // Mistral / Devstral Icon (M logo)
  if (id.includes('mistral') || id.includes('devstral')) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="currentColor"
        width={size}
        height={size}
        className={className}
      >
        <path d="M2 4h4v4H2zM2 10h4v4H2zM2 16h4v4H2zM8 4h4v4H8zM14 4h4v4h-4zM20 4h-2v4h2zM8 16h4v4H8zM14 16h4v4h-4zM8 10h4v4H8zM14 10h4v4h-4z"/>
      </svg>
    );
  }

  // Fallback: Cubo Generico
  return (
    <svg
      fill="none"
      height={size}
      width={size}
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      className={className}
    >
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
      <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
      <line x1="12" y1="22.08" x2="12" y2="12"/>
    </svg>
  );
};

function ModelDropdown({ model, onModelChange, isOpen, onToggle, isLoading }) {
  const dropdownRef = useRef(null);
  const [modelOptions, setModelOptions] = useState([]);

  // Load base models from API (Google, Ollama)
  useEffect(() => {
    const loadModels = async () => {
      try {
        const response = await getAuthorizedFetch()(`${config.agentUrl}/api/config/models`, {
          headers: getBeHeaders()
        });
        if (!response.ok) {
          throw new Error(`Failed to load models: ${response.status}`);
        }
        const data = await response.json();
        
        // Convert API format to dropdown format
        const options = data.models.map(m => ({
            label: m.display_name || m.name,
            value: m.name,
            provider: m.provider,
            description: m.description
          }));
        
        setModelOptions(options);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading models:', error);
        // Fallback to default models
        setModelOptions([
          { label: 'Gemini 2.5 Flash', value: 'gemini-2.5-flash', provider: 'google' },
          { label: 'Gemini 2.5 Pro', value: 'gemini-2.5-pro', provider: 'google' }
        ]);
      }
    };
    
    loadModels();
  }, []);

  const currentModel = modelOptions.find(m => m.value === model) || modelOptions[0] || { label: 'Loading...', value: '' };

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onToggle(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isOpen, onToggle]);

  const handleModelSelect = (value) => {
    onModelChange(value);
    onToggle(false);
  };

  return (
    <div className="ai-mode-dropdown-container model-dropdown-container" ref={dropdownRef}>
      <button
        className={`ai-mode-toggle model-toggle ${isOpen ? 'open' : ''} ${isLoading ? 'icon-only' : ''}`}
        disabled={isLoading || modelOptions.length === 0}
        onClick={() => onToggle(!isOpen)}
        title={currentModel.label}
        type="button"
      >
        <ModelIcon modelId={currentModel.value} size={14} />

        <span className="button-text">{currentModel.label}</span>
        <svg
          className={`dropdown-arrow ${isOpen ? 'open' : ''}`}
          width="12"
          height="12"
          viewBox="0 0 12 12"
        >
          <path
            d="M3 4.5l3 3 3-3"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {isOpen && (
        <div className="ai-mode-dropdown-menu">
          {modelOptions.map((option) => (
            <button
              key={option.value}
              className={`ai-mode-option ${model === option.value ? 'active' : ''}`}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleModelSelect(option.value);
              }}
              type="button"
            >
              <div className="ai-mode-option-content">
                <ModelIcon modelId={option.value} size={20} />

                <div className="ai-mode-option-text">
                  <span className="ai-mode-option-label">{option.label}</span>
                </div>
              </div>
              {model === option.value && (
                <svg
                  className="ai-mode-check"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                >
                  <path
                    d="M13 4l-7 7-3-3"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default ModelDropdown;
